import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Input, Button, Alert } from "antd";
import { useAuth } from "../../context/AuthContext";
import "../Login/Login.css";

// Reuses Login.css's classes (login-page, login-card, etc.) — same visual
// language, different form: this one is "set your own password", not "sign in".
export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [invite, setInvite] = useState(undefined); // undefined = loading, null = invalid, object = valid
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`/api/auth/invite/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => setInvite(data))
      .catch(() => setInvite(null));
  }, [token]);

  if (user) return <Navigate to={`/${user.tenant}`} replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/auth/invite/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      const loggedInUser = await refresh();
      navigate(`/${loggedInUser?.tenant || data.user.tenant}`, { replace: true });
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div>
        <div className="login-brand">
          <div className="login-wordmark">
            bim<em>formation</em>
          </div>
          <div className="login-tagline">Digital Infrastructure Platform</div>
        </div>

        <div className="login-card">
          {invite === undefined && (
            <div className="login-card-sub">Checking your invite…</div>
          )}

          {invite === null && (
            <Alert
              type="error"
              title="This invite link is invalid or has expired. Ask whoever invited you to send a new one."
              showIcon
            />
          )}

          {invite && (
            <form onSubmit={handleSubmit}>
              <div className="login-card-title">Welcome{invite.name ? `, ${invite.name}` : ""}</div>
              <p className="login-card-sub">Set a password for {invite.email} to finish setting up your account.</p>

              {error && <Alert className="login-error" type="error" title={error} showIcon />}

              <div className="login-field">
                <label htmlFor="invite-password">Password</label>
                <Input.Password
                  id="invite-password"
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              <div className="login-field">
                <label htmlFor="invite-confirm">Confirm password</label>
                <Input.Password
                  id="invite-confirm"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <Button
                className="login-submit"
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                Set password &amp; continue
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
